<!-- The scrim and the watermark, drawn over a picture in CSS.
  --
  -- One component because there are now two surfaces that have to show the same
  -- marks: the drop zone thumbnail, which has previewed them since the scrim
  -- existed, and the stage, which is where the picture is actually worked on.
  -- Writing it twice is the specific mistake this avoids -- the tiling step, the
  -- outline colour and the size-off-the-short-edge rule would have to agree
  -- between two files forever, and the file the visitor downloads is drawn by a
  -- third implementation in lib/imageTransform.js as it is.
  --
  -- An approximation on purpose. The real marks are drawn against the image's
  -- own pixels; these are drawn against the rendered box. They agree because
  -- both size everything off the SHORT edge.
  --
  -- The caller passes the box because only the caller knows it: the drop zone
  -- measures its <img>, the stage computes a frame. -->
<template>
    <span v-if="scrim" class="wm-scrim" aria-hidden="true"
          :style="{backgroundColor: uiStore.overlayColor, opacity: uiStore.overlayOpacity}"></span>
    <span v-if="watermarkStamps.length || logoStamps.length || captionStamps.length"
          class="wm-layer" aria-hidden="true">
        <span v-for="(stamp, i) in watermarkStamps" :key="'t' + i"
              class="wm-text" :style="stamp">{{ uiStore.watermarkText }}</span>
        <img v-for="(stamp, i) in logoStamps" :key="'l' + i"
             class="wm-logo" :src="logo.objectUrl" :style="stamp" alt="" />
        <span v-for="(stamp, i) in captionStamps" :key="'c' + i"
              class="wm-caption" :style="stamp.style">{{ stamp.text }}</span>
    </span>

    <!-- TYPING ON THE PICTURE.
      --
      -- Reported from an iPhone, watched over her shoulder: tapping a caption
      -- area scrolled the page down to a text field, and the picture she was
      -- captioning went off the top of the screen. So she was typing words for
      -- a picture she could no longer see -- which is the one thing the live
      -- preview exists to prevent.
      --
      -- This is the SAME BOX the lines above are drawn in, with the same font,
      -- the same size and the same outline, because both are styled from one
      -- layoutCaption() result. Not a second renderer: while a slot is being
      -- edited its static lines stand down and this takes their place, so
      -- there is never more than one thing drawing a caption.
      --
      -- OUTSIDE .wm-layer, which is aria-hidden and pointer-events:none --
      -- correct for a decoration and fatal for something you have to tap. -->
    <div v-if="editable"
         ref="editorEl"
         class="wm-caption wm-editor"
         :style="editable.style"
         contenteditable="plaintext-only"
         role="textbox"
         :data-caption-editor="editable.slot"
         :aria-label="$t('crop.captionArea', { n: areaNumber(editable.slot) })"
         spellcheck="false"
         @input="onInput"
         @blur="$emit('done')"
         @keydown.esc.prevent="blurEditor"
         @pointerdown.stop
         @click.stop></div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onUnmounted } from "vue"
import { useUiElements } from "../store/uiElements"
import { MIN_WATERMARK_SIZE, MAX_WATERMARK_SIZE, WATERMARK_COLOURS, fontStack,
         MIN_LOGO_SIZE, MAX_LOGO_SIZE } from "../lib/watermark"
import { logo, hasLogo } from "../lib/logo"
import { MEME_SLOTS, memeSlots, memeStoreKeys, MEME_COLOURS, MEME_OUTLINE_RATIO, layoutCaption, captionTops,
         captionCentre, captionMeasurer, memeFontStack, memeFontWeight,
         memeFontSpec, memeOutline } from "../lib/meme"

/* `text` carries what was typed; `done` says the editor lost focus. The stage
 * owns both -- this component draws, it does not decide what is being edited. */
const emit = defineEmits(["text", "done"])

const props = defineProps({
    // The rendered picture box, in CSS pixels.
    w: {type: Number, default: 0},
    h: {type: Number, default: 0},
    // Whether the bytes underneath already carry these marks. They do between a
    // download and the next edit, and drawing them again over the top would be
    // darker, or signed twice.
    baked: {type: Boolean, default: false},
    // The drop zone hides everything when the picture will not decode.
    disabled: {type: Boolean, default: false},
    /* Which caption slot is being typed into, or null.
     *
     * A prop rather than state of its own because the STAGE owns this: the
     * same component also draws the drop zone thumbnail, which is a picture
     * of the marks and not a place to work, and it passes nothing. */
    editing: {type: String, default: null},
    /* The picture's TRUE width over its height, when the caller knows it.
     *
     * Only the captions use it, and only because they are the one mark whose
     * layout depends on the shape of the picture rather than just its size.
     * w and h above are the rendered box in whole CSS pixels, so their ratio is
     * a rounded version of the real one -- fitting a 1600x1067 photograph into
     * a 336-wide box gives 336/224, which is 1.5000 against a true 1.4995. The
     * canvas asks layoutCaption() with the unrounded shape, and a line sitting
     * within that fraction of the wrap limit could then break in one place on
     * screen and another in the file. Passing the decoded dimensions removes
     * the larger half of that gap; see lib/meme.js for what is left of it.
     */
    aspect: {type: Number, default: 0},
})

const uiStore = useUiElements()

const live = computed(() => !props.baked && !props.disabled && !!props.w && !!props.h)
const scrim = computed(() => live.value && uiStore.overlayOpacity > 0)

// One canvas, reused, purely to measure. Tiling has to step by the width of
// the words or long text overlaps itself.
let ruler = null
function rulerCtx() {
	if (!ruler) ruler = document.createElement("canvas").getContext("2d")
	return ruler
}
function textWidth(text, px, key) {
	if (!rulerCtx()) return px * text.length * 0.55
	// The same face the stamps are drawn in, or tiling steps by the wrong width
	// and long text overlaps itself.
	ruler.font = `600 ${px}px ${fontStack(key)}`
	return ruler.measureText(text).width
}

// Black under light lettering, white under dark, matching outlineFor() in
// lib/imageTransform.js so the preview and the file agree.
const outline = computed(() => {
	// The fallback comes from the palette in lib/watermark.js rather than being
	// written here: tests/test_palette.py forbids a raw hex in a .vue, because
	// interface colour has to follow the theme and this one must not.
	const hex = uiStore.watermarkColor || WATERMARK_COLOURS[0].hex
	const n = parseInt(hex.slice(1), 16)
	if (!Number.isFinite(n)) return "rgba(0,0,0,0.45)"
	const luma = 0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)
	return luma > 140 ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.5)"
})

const watermarkStamps = computed(() => {
	if (!live.value) return []
	// One mark or the other, matching what applyTransforms() actually sends.
	if (logo.mode === "logo") return []
	const text = (uiStore.watermarkText || "").trim()
	const w = props.w
	const h = props.h
	if (!text || !w || !h) return []

	const short = Math.min(w, h)
	const asked = uiStore.watermarkSize || 0
	const size = Math.min(MAX_WATERMARK_SIZE, Math.max(MIN_WATERMARK_SIZE, asked))
	const px = Math.max(9, Math.round(short * size))
	const margin = Math.round(short * 0.04)
	const base = {
		fontFamily: fontStack(uiStore.watermarkFont),
		fontSize: px + "px",
		color: uiStore.watermarkColor,
		opacity: uiStore.watermarkOpacity,
		textShadow: `0 0 ${Math.max(1, px / 12)}px ${outline.value}`,
	}
	const at = (extra) => ({ ...base, ...extra })
	const place = uiStore.watermarkPosition

	if (place === "tiled") {
		const stepX = textWidth(text, px, uiStore.watermarkFont) + px * 1.6
		const stepY = px * 3.2
		const reach = Math.ceil(Math.hypot(w, h))
		const out = []
		for (let y = -reach; y <= reach; y += stepY) {
			for (let x = -reach; x <= reach; x += stepX) {
				out.push(at({
					left: "50%", top: "50%",
					transform: `rotate(-30deg) translate(${x}px, ${y}px) translate(-50%, -50%)`,
				}))
			}
		}
		// A guard, not a policy: a very long string in a very small box could
		// otherwise ask for thousands of nodes on every keystroke.
		return out.length > 400 ? out.slice(0, 400) : out
	}

	if (place === "diagonal") {
		const angle = Math.atan2(h, w) * 180 / Math.PI
		return [at({
			left: "50%",
			top: "50%",
			transform: `translate(-50%, -50%) rotate(${-angle}deg)`,
		})]
	}
	if (place === "centre") {
		return [at({ left: "50%", top: "50%", transform: "translate(-50%, -50%)" })]
	}

	const style = {}
	style[place.endsWith("right") ? "right" : "left"] = margin + "px"
	style[place.startsWith("bottom") ? "bottom" : "top"] = margin + "px"
	return [at(style)]
})

/* Captions, and the one place in this file that is NOT an approximation.
 *
 * Everything else here re-implements lib/imageTransform.js's arithmetic and
 * agrees with it because both size off the short edge. That is fine for one
 * line in a corner. It would not be fine for a caption, which wraps: two
 * hand-agreed wrapping algorithms disagree on some sentence nobody thought to
 * try, and then the preview promises a picture the download does not deliver.
 *
 * So this asks lib/meme.js the question the canvas asks it, with the same
 * measurer, and gets the same lines back. What differs between the two callers
 * is which short edge they multiply by -- and, to about one part in four
 * thousand, how they measured the picture's shape; lib/meme.js's header is
 * exact about that. Nothing here decides anything.
 */
/* THE LAYOUT IS MEASURED AGAINST WHATEVER FACE THE CANVAS HAS RIGHT NOW.
 *
 * layoutCaption() shrinks the words until they fit, and it asks a canvas how
 * wide they are. A canvas asked for a webfont it has not got answers with the
 * FALLBACK, and the difference is not small -- measured, "ORA TRISTE" at 40px:
 *
 *     fallback sans   240.0 px
 *     Anton           162.0 px      (the default face)
 *     Bebas Neue      136.9 px
 *     Archivo Black   271.1 px
 *
 * So a caption laid out before its face arrives is fitted to the wrong
 * metrics, by up to half its width, and nothing brought it back: neither
 * computed below depends on font loading, so the wrong fit simply stays.
 *
 * WHICH IS HOW ONE AREA ENDS UP LOOKING LIKE A DIFFERENT TYPEFACE FROM THE
 * OTHER, reported exactly that way. The two areas are laid out by two separate
 * computeds with two different dependency sets -- editable reads only the slot
 * being edited, captionStamps reads both -- so typing in one while the other
 * is open recomputes one and not the other. If the face arrives between those
 * two moments, the areas are fitted to two different fonts. One selector, two
 * answers.
 *
 * The bake was never exposed to this: ensureMemeFont() awaits the face before
 * drawing. That is what made it a PREVIEW-only disagreement, which is the one
 * thing lib/meme.js exists to prevent.
 */
const fontEpoch = ref(0)
let awaited = null

function awaitFace(key) {
	if (typeof document === "undefined" || !document.fonts || !document.fonts.load) return
	const spec = memeFontSpec(24, key)
	if (document.fonts.check(spec)) return
	awaited = key
	document.fonts.load(spec).then(() => {
		// Only for the face still selected: picking a third one while the
		// second is in flight must not re-fit against the one nobody wants.
		if (awaited === key) fontEpoch.value++
	}).catch(() => { /* the fallback fit is what there is */ })
}

watch(() => uiStore.memeFont, (key) => awaitFace(key), {immediate: true})

const captionStamps = computed(() => {
	if (!live.value) return []
	const w = props.w
	const h = props.h
	if (!w || !h) return []
	// Any area with words in it is a reason to draw; four named checks here is
	// how area 3 comes to preview as nothing at all.
	if (!MEME_SLOTS.some((sl) => (uiStore[memeStoreKeys(sl).text] || "").trim())) return []
	const ctx = rulerCtx()
	// No 2D context means no preview -- and no download either, since the bake
	// needs one too. Guessing at the widths would put the preview and the file
	// in different places, which is the failure this whole arrangement avoids.
	if (!ctx) return []

	// Read, never used: it is the dependency that re-fits these words once the
	// chosen face has actually arrived. See fontEpoch above.
	void fontEpoch.value
	const short = Math.min(w, h)
	// The true shape when the caller knows it, the rendered box's when it does
	// not -- the drop-zone thumbnail mounts this component too.
	const aspect = props.aspect > 0 ? props.aspect : w / h
	const measure = captionMeasurer(ctx, uiStore.memeFont)
	const out = []

	// Colour and size are per area, so they are read inside the loop. The
	// fallback comes from the palette in lib/meme.js rather than being written
	// here: tests/test_palette.py forbids a raw hex in a .vue.
	for (const slot of memeSlots(uiStore.memeZones)) {
		// The store fields for this slot, from lib/meme.js. A ternary per
		// property worked for two areas and does not generalise: four areas
		// would be four chained conditionals on every field, and the failure
		// mode is an area silently borrowing another one's colour.
		const k = memeStoreKeys(slot)
		const box = uiStore[k.box]
		const colour = uiStore[k.color] || MEME_COLOURS[0].hex
		const stroke = memeOutline(colour)
		const layout = layoutCaption({
			text: uiStore[k.text],
			aspect,
			measure,
			size: uiStore[k.size],
			upper: uiStore.memeUpper,
			box,
			slot,
		})
		if (!layout) continue
		const px = layout.size * short
		/* The slot being edited draws itself, below. Letting both render puts
		 * the same words on the picture twice, half a pixel apart. */
		const underEdit = props.editing === slot
		const tops = captionTops(slot, layout)
		// Positioned and centred on the rectangle rather than on the picture,
		// which for the default box is the same thing.
		const left = (layout.box.x * short) + "px"
		const width = (layout.box.w * short) + "px"
		if (underEdit) continue
		for (let i = 0; i < layout.lines.length; i++) {
			out.push({
				text: layout.lines[i],
				style: {
					top: (tops[i] * short) + "px",
					left,
					width,
					right: "auto",
					fontFamily: memeFontStack(uiStore.memeFont),
					fontWeight: memeFontWeight(uiStore.memeFont),
					fontSize: px + "px",
					// The line box carries the leading, which is how the canvas
					// side places its baselines too -- see the `lead` term in
					// drawMeme(). Half above, half below, the way CSS does it.
					lineHeight: (layout.lineHeight * short) + "px",
					color: colour,
					// -webkit-text-stroke rather than a text-shadow because it
					// is centred on the glyph outline exactly as canvas
					// strokeText() is, and `paint-order` below puts the fill
					// back on top the same way.
					WebkitTextStrokeWidth: Math.max(1, px * MEME_OUTLINE_RATIO) + "px",
					WebkitTextStrokeColor: stroke,
				},
			})
		}
	}
	return out
})

/* The editor's own style, from the SAME layoutCaption() call the lines use.
 *
 * This is the whole reason the words do not jump when you start typing: the
 * box, the font, the weight, the size after shrink-to-fit, the leading, the
 * colour and the outline are all read off one layout, and lib/meme.js remains
 * the only thing in the application that decides where a caption sits.
 *
 * The SIZE keeps updating while typing -- a long line shrinks as it is
 * written, which is what the finished picture will do -- and that is safe to
 * re-render because it is a style, not content: changing it does not move the
 * caret. The TEXT is deliberately not bound; see onInput below.
 */
/* Area 1..4, the way the reader counts them -- the same name CropSurface
 * gives the rectangle this editor sits inside, so the two cannot disagree
 * about which area is being typed into. */
function areaNumber(slot) {
	return MEME_SLOTS.indexOf(slot) + 1
}

const editorEl = ref(null)

const editable = computed(() => {
	if (!props.editing) return null
	const slot = props.editing
	// Same dependency, same reason -- and this computed is the half that made
	// the two areas disagree, because it tracks only the slot it is editing.
	void fontEpoch.value
	const k = memeStoreKeys(slot)
	const box = uiStore[k.box]
	const short = Math.min(props.w, props.h)
	if (!short) return null
	const aspect = props.aspect || (props.w / props.h)
	const ctx = rulerCtx()
	if (!ctx) return null
	const colour = uiStore[k.color] || MEME_COLOURS[0].hex
	const text = uiStore[k.text]
	const layout = layoutCaption({
		/* AN EMPTY SLOT STILL NEEDS A SIZE TO TYPE AT, and `text || "A"` was
		 * not enough to give it one.
		 *
		 * Delete the last character of a contenteditable and Chrome leaves a
		 * <br> behind, so innerText is "\n" -- which is TRUTHY, so the
		 * fallback never fired, layoutCaption() normalised the newline away to
		 * "" and returned null, and this computed returned null with it. The
		 * editor then unmounted mid-keystroke: the caret vanished, the blur
		 * that removal fires was read as "done", and the next thing typed went
		 * nowhere. Reported as the application getting stuck.
		 *
		 * So the question is whether there is anything to LAY OUT, not whether
		 * the string is truthy. */
		text: (text || "").trim() ? text : "A",
		aspect,
		measure: captionMeasurer(ctx, uiStore.memeFont),
		size: uiStore[k.size],
		upper: uiStore.memeUpper,
		box,
		slot,
	})
	if (!layout) return null
	const px = layout.size * short
	const tops = captionTops(slot, layout)
	return {
		slot,
		style: {
			position: "absolute",
			top: (tops[0] * short) + "px",
			left: (layout.box.x * short) + "px",
			width: (layout.box.w * short) + "px",
			fontFamily: memeFontStack(uiStore.memeFont),
			fontWeight: memeFontWeight(uiStore.memeFont),
			fontSize: px + "px",
			lineHeight: (layout.lineHeight * short) + "px",
			color: colour,
			WebkitTextStrokeWidth: Math.max(1, px * MEME_OUTLINE_RATIO) + "px",
			WebkitTextStrokeColor: memeOutline(colour),
			// The store holds what was typed; the capitals are a rendering
			// choice, so the editor shows them the way the file will.
			textTransform: uiStore.memeUpper ? "uppercase" : "none",
			caretColor: colour,
		},
	}
})

/* WRITE OUT, NEVER BACK IN.
 *
 * The one rule that makes a contenteditable survivable: the element owns its
 * own text while it has focus, and nothing re-assigns that text underneath
 * the person typing. Binding it to the store would re-render on every
 * keystroke and put the caret back at the start of the line -- the classic
 * failure, and the reason this is not a v-model.
 *
 * Seeded once when editing begins (the watch below) and read on every input.
 * innerText rather than textContent: it is the one that turns the browser's
 * line breaks back into "\n", which is what the canvas renderer expects.
 */
function onInput() {
	const el = editorEl.value
	if (!el) return
	const raw = (el.innerText || "").replace(/\u00a0/g, " ")
	/* WHITESPACE-ONLY IS EMPTY, and nothing else is touched.
	 *
	 * An emptied contenteditable reads back as "\n" rather than "", and a
	 * caption of one newline is not a caption -- it would travel into the
	 * store, the hint would not come back, and usableMeme() would have to trim
	 * it out again at the other end.
	 *
	 * NOT a plain .trim(): the editor owns its own text while it has focus, so
	 * trimming here would still leave the visitor's spaces on screen while the
	 * preview lost them -- and a trailing space is how the NEXT word starts.
	 * Only the all-whitespace case collapses. */
	emit("text", {slot: props.editing, text: raw.trim() ? raw : ""})
}

/* THE KEYBOARD MOVES THE PAGE, AND preventScroll DOES NOT GOVERN THAT.
 *
 * Third report of the same thing, and the first two fixes were both aimed at
 * the wrong cause. Ruled out by reading the shipped bundle: nothing of ours
 * scrolls on this path -- no focus() on a panel field anywhere in src/, and
 * every scrollIntoView belongs to the downloadables, the batch table, the drop
 * zone or the trimmer. preventScroll already stops the focus() jump.
 *
 * What is left is the platform: the soft keyboard opens, visualViewport
 * shrinks, and the browser scrolls the focused element into whatever space
 * remains. That happens after focus() has returned and is not ours to refuse.
 *
 * So this does not fight it -- it settles it. Once the viewport has changed
 * size, either the PICTURE is still visible, in which case the page goes back
 * exactly where the reader left it, or it is behind the keyboard, in which
 * case it moves by EXACTLY the overlap and no further. Never to the top,
 * which is what the last attempt did and what was reported as confusing.
 *
 * The picture, not the caption. Keeping the caption clear of the keys is what
 * this did first, and it left the photograph underneath them -- see settle().
 *
 * NOT VERIFIED ON A DEVICE. There is no Android here and a soft keyboard
 * cannot be opened in a headless browser, so the resize this hangs off never
 * fires in any suite. The arithmetic is testable and is; the platform
 * behaviour it compensates for is not.
 */
const KEYBOARD_MARGIN = 8

/* Cleared and re-armed on every edit, so two editors never hold the page at
 * once -- tapping the other area while the keyboard is up would otherwise
 * leave the first one's timers running against an element that has gone. */
let releaseHold = null

function holdPosition(el) {
	if (typeof window === "undefined") return
	if (releaseHold) releaseHold()
	const vv = window.visualViewport
	const startY = window.scrollY
	const timers = []

	const stop = () => {
		while (timers.length) clearTimeout(timers.pop())
		if (vv) vv.removeEventListener("resize", soon)
		if (releaseHold === stop) releaseHold = null
	}

	const settle = () => {
		if (!el.isConnected) { stop(); return }

		/* THE PICTURE IS WHAT HAS TO BE VISIBLE, NOT THE CAPTION.
		 *
		 * The first version of this kept the EDITOR clear of the keyboard,
		 * which it did -- and the photograph the caption sits on was
		 * underneath it. Reported with a screenshot: the dashed rectangle
		 * sitting exactly on the top row of keys and nothing of the picture
		 * below it. Editing in place is worth nothing if the place cannot be
		 * seen, so the thing measured is the picture box. */
		const box = el.closest("[data-picture-box]") || el
		const top = vv ? vv.offsetTop : 0
		const bottom = top + (vv ? vv.height : window.innerHeight) - KEYBOARD_MARGIN

		let want = box.getBoundingClientRect()
		if (want.height > bottom - top) {
			/* Taller than what is left of the screen, so something has to be
			 * off it. Keep the band around the caption: that is the half
			 * being worked on, and it is inside the picture either way. */
			const r = el.getBoundingClientRect()
			const mid = (r.top + r.bottom) / 2
			const h = bottom - top
			let a = Math.max(want.top, mid - h / 2)
			if (a + h > want.bottom) a = want.bottom - h
			want = {top: a, bottom: a + h}
		}

		/* WHERE THE READER LEFT IT WINS, but only when it still works.
		 *
		 * Going back to startY is the right answer when the browser's own jump
		 * moved the page for no reason the reader asked for. It is the wrong
		 * answer once the keyboard has taken half the screen, because the
		 * position they left is a position the picture no longer fits in. So
		 * it is offered rather than assumed: only if the whole picture would
		 * still be visible there. */
		const back = window.scrollY - startY
		if (want.top + back >= top && want.bottom + back <= bottom) {
			if (Math.abs(back) > 1) window.scrollTo(0, startY)
			return
		}
		if (want.top >= top && want.bottom <= bottom) return
		/* Behind the keyboard, or above the top. Move by the shortfall only --
		 * never to the top of the page, which is what an earlier attempt did
		 * and what was reported as confusing. */
		const by = want.bottom > bottom ? want.bottom - bottom : want.top - top
		if (Math.abs(by) > 1) window.scrollBy(0, by)
	}

	/* KEEP LISTENING, AND SETTLE MORE THAN ONCE.
	 *
	 * The first version took the first resize and stopped. A soft keyboard
	 * does not arrive in one step: Android reports the viewport shrinking
	 * several times while the panel slides up, so settling on the first of
	 * those measured a screen that was still half there -- and then the
	 * browser scrolls the focused element into view AFTER the last of them,
	 * which put the picture back behind the keys with nothing left watching.
	 *
	 * So every resize re-arms a short settle, and two fixed ones run after the
	 * animation is over whatever the platform said. Each pass is idempotent --
	 * it either finds the picture visible and does nothing, or moves by the
	 * shortfall -- so running it four times costs nothing and being late costs
	 * nothing either. It all stops after a second and a half; past that, any
	 * movement is the reader's own. */
	const soon = () => {
		timers.push(setTimeout(settle, 120))
	}
	if (vv) vv.addEventListener("resize", soon)
	timers.push(setTimeout(settle, 400))
	timers.push(setTimeout(settle, 900))
	timers.push(setTimeout(stop, 1600))
	releaseHold = stop
}

function blurEditor() {
	if (editorEl.value) editorEl.value.blur()
}

onUnmounted(() => { if (releaseHold) releaseHold() })

watch(() => props.editing, async (slot) => {
	if (!slot) return
	await nextTick()
	const el = editorEl.value
	if (!el) return
	el.innerText = uiStore[memeStoreKeys(slot).text] || ""
	/* preventScroll, because the browser's own idea of bringing a focused
	 * element into view is a jump -- and this element is already in view: the
	 * reader just tapped it. Android was reported doing exactly that, and it
	 * reads as the page running away from the finger. Falls back to a plain
	 * focus() where the option is not supported, since a caret somewhere is
	 * better than no caret. */
	try { el.focus({preventScroll: true}) } catch (e) { el.focus() }
	holdPosition(el)
	/* The caret goes to the END, which is where somebody adding to a caption
	 * expects it. Without this it lands at the start and typing runs
	 * backwards into what is already there. */
	try {
		const r = document.createRange()
		r.selectNodeContents(el)
		r.collapse(false)
		const sel = window.getSelection()
		sel.removeAllRanges()
		sel.addRange(r)
	} catch (e) { /* no selection API: the field still works */ }
})

/* The logo, previewed the same way and with the same arithmetic as drawLogo()
 * in lib/imageTransform.js: fitted INTO a box whose side is a fraction of the
 * short edge, keeping its own proportions. Scaling to the box instead would
 * squash a wide logo on a tall photograph, and the preview would be telling a
 * comfortable lie about the file. */
const logoStamps = computed(() => {
	if (!live.value) return []
	if (logo.mode !== "logo" || !hasLogo()) return []
	const w = props.w
	const h = props.h
	if (!w || !h) return []

	const short = Math.min(w, h)
	const size = Math.min(MAX_LOGO_SIZE, Math.max(MIN_LOGO_SIZE, logo.size || 0))
	const box = Math.max(8, short * size)
	const scale = Math.min(box / logo.width, box / logo.height)
	const lw = Math.round(logo.width * scale)
	const lh = Math.round(logo.height * scale)
	const margin = Math.round(short * 0.04)
	const base = {width: lw + "px", height: lh + "px", opacity: uiStore.watermarkOpacity}
	const at = (extra) => ({ ...base, ...extra })
	const place = uiStore.watermarkPosition

	if (place === "tiled") {
		const stepX = lw + short * 0.06
		const stepY = lh + short * 0.06
		const reach = Math.ceil(Math.hypot(w, h))
		const out = []
		for (let y = -reach; y <= reach; y += stepY) {
			for (let x = -reach; x <= reach; x += stepX) {
				out.push(at({
					left: "50%", top: "50%",
					transform: `rotate(-30deg) translate(${x}px, ${y}px) translate(-50%, -50%)`,
				}))
			}
		}
		return out.length > 200 ? out.slice(0, 200) : out
	}
	if (place === "diagonal") {
		const angle = Math.atan2(h, w) * 180 / Math.PI
		return [at({
			left: "50%",
			top: "50%",
			transform: `translate(-50%, -50%) rotate(${-angle}deg)`,
		})]
	}
	if (place === "centre") {
		return [at({left: "50%", top: "50%", transform: "translate(-50%, -50%)"})]
	}
	const style = {}
	style[place.endsWith("right") ? "right" : "left"] = margin + "px"
	style[place.startsWith("bottom") ? "bottom" : "top"] = margin + "px"
	return [at(style)]
})
</script>

<style scoped>
.wm-scrim {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
}

/* Over the scrim, because the real one is drawn after it: darkening a picture
 * and then signing it is the case both were built for. */
.wm-layer {
    position: absolute;
    inset: 0;
    overflow: hidden;
    pointer-events: none;
    border-radius: inherit;
    z-index: 1;
}

/* position: absolute is the load-bearing line. The host's own img rule sets
 * position: static in at least one place -- .file-preview.large img, which
 * gives the thumbnail its shape -- and a static element ignores left and top,
 * so every stamp landed in the top left corner with only its translate
 * applied. Scoping keeps the host's rules off these elements now, but the
 * declaration stays explicit rather than inherited by luck.
 *
 * max-width and max-height for the same reason: that rule caps the picture at
 * 46vh, and a logo is not the picture. */
.wm-layer img.wm-logo {
    position: absolute;
    transform-origin: center;
    object-fit: contain;
    max-width: none;
    max-height: none;
}

.wm-text {
    position: absolute;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    transform-origin: center;
}

/* One span per LINE, already broken by layoutCaption().
 *
 * white-space: nowrap is the load-bearing declaration, and not for the reason
 * it is on .wm-text above. Without it CSS would be free to re-wrap a line that
 * came out a fraction wider than the box -- and a second opinion about where
 * the words break is exactly what this component was rewritten to avoid. The
 * layout function decides; the stylesheet renders what it decided. .wm-layer
 * clips, so the worst case is a hair off the edge rather than a preview that
 * disagrees with the file.
 *
 * left/right rather than a translate so that text-align does the centring: the
 * canvas centres with textAlign = "center" about dw / 2, and a full-width
 * centred line box is the same position without a second calculation.
 *
 * paint-order puts the fill back over the stroke, which is what drawMeme()
 * does by calling strokeText() before fillText(). Without it the keyline eats
 * half the weight of every letter. */
.wm-caption {
    position: absolute;
    left: 0;
    right: 0;
    margin: 0;
    text-align: center;
    white-space: nowrap;
    paint-order: stroke fill;
}

/* AFTER .wm-caption, AND THAT IS THE WHOLE RULE.
 *
 * Both are single-class selectors, so they have the same specificity and the
 * later one wins. Declared first, `white-space: pre-wrap` below lost to
 * `nowrap` above -- which is right for a static line, because layoutCaption()
 * has already decided where that line ends and re-wrapping it would put the
 * break somewhere else. The editor is the opposite case: it holds the WHOLE
 * caption and has to wrap it.
 *
 * Measured with it the wrong way round: a caption that needed four lines drew
 * as one 22px line in a 70px box and ran off both sides of the picture.
 *
 * The editor is a caption that can be typed into. Everything that makes it
 * LOOK like the caption comes from the inline style, which is computed from
 * the same layout the static lines use; the rules here are only the ones that
 * make it editable and keep it out of its own way. */
.wm-editor {
    z-index: 3;
    outline: none;
    text-align: center;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    /* The one thing .wm-layer must not pass on: this has to be tappable. */
    pointer-events: auto;
    /* iOS will not put a caret in a box it thinks is empty and zero-height. */
    min-height: 1em;
    cursor: text;
}

</style>
